import { useCallback, useEffect, useRef, useState } from 'react';

// Retain the last successful read while refreshing the same context. A new
// context or a newer request must never receive an older request's result.
export default function useScopedLoad(scope, load) {
  const [state, setState] = useState({ scope: null, data: null, loading: false, error: null, hasLoaded: false });
  const request = useRef(0);
  const refresh = useCallback(async () => {
    const id = ++request.current;
    if (!scope) return;
    setState(previous => ({
      scope, data: previous.scope === scope ? previous.data : null,
      hasLoaded: previous.scope === scope && previous.hasLoaded,
      loading: true, error: null,
    }));
    try {
      const data = await load();
      if (request.current === id) setState({ scope, data, loading: false, error: null, hasLoaded: true });
    } catch (error) {
      if (request.current === id) setState(previous => ({ ...previous, loading: false, error }));
    }
  }, [scope, load]);
  useEffect(() => {
    refresh();
    return () => { request.current += 1; };
  }, [refresh]);
  const updateData = useCallback(update => setState(previous => previous.scope === scope
    ? { ...previous, data: typeof update === 'function' ? update(previous.data) : update }
    : previous), [scope]);
  const current = scope && state.scope === scope;
  return {
    data: current ? state.data : null,
    loading: !!scope && (!current || state.loading),
    error: current ? state.error : null,
    hasLoaded: !!current && state.hasLoaded,
    refresh, updateData,
  };
}
